<script setup>
import { computed, onMounted, ref } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { useLeadStore } from "@/stores/leadStore";
import LeadsModal from "@/components/modal/leads-modal.vue";

const title = "Leads";
const text = "CRM";
const text1 = "Leads Grid";

const store = useLeadStore();
const editItem = ref(null);
const search = ref("");
const sort = ref("recent");
const pipelineFilter = ref("");

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function openModal(item = null) {
  editItem.value = item;
  const el = document.getElementById("add_leads");
  Modal.getOrCreateInstance(el)?.show();
}

async function removeLead(item) {
  if (!item?.id) return;
  if (!window.confirm(`Delete lead "${item.name}"?`)) return;
  await store.remove(item.id);
}

function exportCsv() {
  const rows = filteredLeads.value;
  const header = ["Name", "Company", "Pipeline", "Stage", "Owner", "Email", "Phone", "Value", "Created"];
  const body = rows.map((lead) => [
    lead.name || "",
    lead.company_name || "",
    lead.pipeline?.name || "",
    lead.stage?.name || "",
    ownerLabel(lead),
    lead.email || "",
    lead.phone || "",
    `${lead.currency || "KES"} ${Number(lead.amount || 0).toFixed(2)}`,
    lead.created_at || "",
  ]);
  const csv = [header, ...body]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `crm-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const pipelines = computed(() => {
  const unique = new Map();
  (store.items || []).forEach((lead) => {
    if (lead.pipeline?.id) unique.set(lead.pipeline.id, lead.pipeline);
  });
  return Array.from(unique.values());
});

const filteredLeads = computed(() => {
  const q = search.value.trim().toLowerCase();
  let rows = Array.isArray(store.items) ? [...store.items] : [];

  if (pipelineFilter.value) {
    rows = rows.filter((lead) => Number(lead.pipeline_id) === Number(pipelineFilter.value));
  }

  if (q) {
    rows = rows.filter((lead) => {
      const hay = [
        lead.name,
        lead.company_name,
        lead.email,
        lead.phone,
        lead.pipeline?.name,
        lead.stage?.name,
        ownerLabel(lead),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }

  if (sort.value === "name_asc") rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  if (sort.value === "name_desc") rows.sort((a, b) => String(b.name || "").localeCompare(String(a.name || "")));
  if (sort.value === "recent") rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return rows;
});

function initials(name) {
  return String(name || "LD")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function ownerLabel(lead) {
  const owner = lead?.owner;
  if (!owner) return "Unassigned";
  return [owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.email || "Unassigned";
}

function money(lead) {
  return `${String(lead.currency || "KES").toUpperCase()} ${Number(lead.amount || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
}

onMounted(async () => {
  await store.fetchAll({ page: 1, per_page: 100 });
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="me-2 mb-2">
            <div class="d-flex align-items-center border bg-white rounded p-1 me-2">
              <router-link to="/crm/leads-list" class="btn btn-icon btn-sm me-1">
                <i class="ti ti-list-tree"></i>
              </router-link>
              <router-link to="/crm/leads-grid" class="btn btn-icon btn-sm active bg-primary text-white">
                <i class="ti ti-layout-grid"></i>
              </router-link>
            </div>
          </div>

          <div class="me-2 mb-2">
            <button class="btn btn-white d-inline-flex align-items-center" @click="exportCsv">
              <i class="ti ti-file-type-csv me-1"></i>Export CSV
            </button>
          </div>

          <div class="mb-2">
            <button class="btn btn-primary d-flex align-items-center" @click="openModal()" v-can="'Lead.Create'">
              <i class="ti ti-circle-plus me-2"></i>Add Lead
            </button>
          </div>

          <div class="head-icons ms-2">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-3">
          <div class="row g-3 align-items-end">
            <div class="col-lg-5">
              <label class="form-label">Search</label>
              <input v-model.trim="search" class="form-control" placeholder="Search lead, company, owner, email, or phone" />
            </div>
            <div class="col-lg-3">
              <label class="form-label">Pipeline</label>
              <select v-model="pipelineFilter" class="form-select">
                <option value="">All pipelines</option>
                <option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">
                  {{ pipeline.name }}
                </option>
              </select>
            </div>
            <div class="col-lg-3">
              <label class="form-label">Sort</label>
              <select v-model="sort" class="form-select">
                <option value="recent">Recently Added</option>
                <option value="name_asc">Name A to Z</option>
                <option value="name_desc">Name Z to A</option>
              </select>
            </div>
            <div class="col-lg-1">
              <button class="btn btn-outline-secondary w-100" @click="store.fetchAll({ page: 1, per_page: 100 })">
                <i class="ti ti-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div v-if="store.loading && !filteredLeads.length" class="col-12">
          <div class="card">
            <div class="card-body py-5 text-center text-muted">Loading leads...</div>
          </div>
        </div>

        <div v-else-if="!filteredLeads.length" class="col-12">
          <div class="card">
            <div class="card-body py-5 text-center text-muted">No leads match the current filters.</div>
          </div>
        </div>

        <div v-for="lead in filteredLeads" :key="lead.id" class="col-xl-3 col-lg-4 col-md-6 d-flex">
          <div class="card w-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <router-link
                  :to="{ name: 'crm.leads.details', params: { id: lead.id } }"
                  class="avatar avatar-xl avatar-rounded border border-primary text-primary bg-light d-inline-flex align-items-center justify-content-center"
                >
                  <span class="fw-semibold">{{ initials(lead.name) }}</span>
                </router-link>

                <div class="dropdown">
                  <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                    <i class="ti ti-dots-vertical"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li>
                      <button class="dropdown-item rounded-1" @click="openModal(lead)" v-can="'Lead.Edit'">
                        <i class="ti ti-edit me-1"></i>Edit
                      </button>
                    </li>
                    <li>
                      <button class="dropdown-item rounded-1 text-danger" @click="removeLead(lead)" v-can="'Lead.Delete'">
                        <i class="ti ti-trash me-1"></i>Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="text-center mb-3">
                <h6 class="mb-1">
                  <router-link :to="{ name: 'crm.leads.details', params: { id: lead.id } }">
                    {{ lead.name }}
                  </router-link>
                </h6>
                <span class="badge bg-info-transparent fs-10 fw-medium">
                  {{ lead.stage?.name || "No stage" }}
                </span>
              </div>

              <div class="d-flex flex-column gap-2 mb-3">
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-building text-gray-5 me-2"></i>
                  {{ lead.company_name || "No company" }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-git-branch text-gray-5 me-2"></i>
                  {{ lead.pipeline?.name || "No pipeline" }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-currency-dollar text-gray-5 me-2"></i>
                  {{ money(lead) }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-mail-forward text-gray-5 me-2"></i>
                  {{ lead.email || "No email" }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-phone text-gray-5 me-2"></i>
                  {{ lead.phone || "No phone" }}
                </div>
              </div>

              <div class="mt-auto d-flex align-items-center justify-content-between border-top pt-3">
                <span class="text-dark fs-12">{{ ownerLabel(lead) }}</span>
                <span class="text-muted fs-12">{{ formatDate(lead.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <LeadsModal :edit-item="editItem" @saved="store.fetchAll({ page: 1, per_page: 100 })" />
</template>
